import { useEffect } from "react";

type UseOnClickOutside = (
  ref: React.RefObject<HTMLElement | Element> | null,
  handler?: (e: MouseEvent | TouchEvent) => any,
  options?: Options
) => void;

type Options = {
  /**
   * Sets the outside click listeners on or off.
   */
  isActive?: boolean;
};

/** react-toastify renders into a portal under this root; clicks there must not count as “outside”. */
export const REACT_TOASTIFY_ROOT_SELECTOR = ".Toastify";

const defaultOptions = { isActive: true };

/**
 * useOnClickOutside detects a click outside of a given element.
 * @param ref React ref of an element you want to track the clicks around.
 * @param handler Function to call on click outside.
 * @param options
 *
 * @see https://usehooks.com/useOnClickOutside/
 */
export const useOnClickOutside: UseOnClickOutside = (
  ref,
  handler,
  options = defaultOptions
) => {
  useEffect(() => {
    if (!ref || !options.isActive) {
      return;
    }

    const listener = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (!ref.current || ref.current.contains(target)) {
        return;
      }

      if (
        target instanceof Element &&
        target.closest(REACT_TOASTIFY_ROOT_SELECTOR)
      ) {
        return;
      }

      handler?.(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler, options.isActive]);
};
